"use client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PromoService, {
  flattenShowOptions,
  PromoDetail,
  PromoShowOption,
} from "@/app/_services/PromoService";
import OrderService from "@/app/_services/OrderService";
import { toast } from "@/app/_utils/toast";

export const PAGE_SIZE = 25;

export function usePromoDetailModel() {
  const { promoId } = useParams<{ promoId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<PromoDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOptions, setShowOptions] = useState<PromoShowOption[]>([]);
  const [openForm, setOpenForm] = useState(false);

  const [orders, setOrders] = useState<any[]>([]);
  const [orderCount, setOrderCount] = useState(0);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [showIds, setShowIds] = useState<string[]>([]);

  async function loadDetail() {
    if (!promoId) return;
    setIsLoading(true);
    try {
      setData(await PromoService.getPromoDetailAsync(promoId));
    } catch {
      toast.error("Failed to load promo code");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
    // Only needed so the edit dialog can render its show picker.
    PromoService.getUpcomingShowsAsync()
      .then((shows) => setShowOptions(flattenShowOptions(shows)))
      .catch(() => setShowOptions([]));
  }, [promoId]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    if (!promoId) return;
    let cancelled = false;
    setIsOrdersLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await OrderService.getOrdersAsync({
          promo: promoId,
          search: search.trim() || undefined,
          showIds,
          paginationModel: { page, pageSize: PAGE_SIZE },
        });
        if (cancelled) return;
        setOrders(res?.data ?? []);
        setOrderCount(res?.count ?? 0);
      } catch {
        if (!cancelled) toast.error("Failed to load orders");
      } finally {
        if (!cancelled) setIsOrdersLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [promoId, search, showIds, page]);

  function changeSearch(value: string) {
    setSearch(value);
    setPage(0);
  }

  function changeShowIds(ids: string[]) {
    setShowIds(ids);
    setPage(0);
  }

  async function handleToggleActive() {
    if (!data) return;
    try {
      await PromoService.updatePromoCodeAsync(data.promo.id, { active: !data.promo.active });
      toast.success(data.promo.active ? "Promo code deactivated" : "Promo code activated");
      await loadDetail();
    } catch (e: any) {
      toast.error(e?.message || "Failed to update promo code");
    }
  }

  async function handleDelete() {
    if (!data) return;
    try {
      await PromoService.deletePromoCodeAsync(data.promo.id);
      toast.success("Promo code deleted");
      navigate("/admin/promos");
    } catch {
      toast.error("Failed to delete promo code");
    }
  }

  return {
    promo: data?.promo ?? null,
    kpis: data?.kpis ?? null,
    showUsage: data?.shows ?? [],
    isLoading,
    showOptions,
    openForm,
    openEditForm: () => setOpenForm(true),
    closeForm: () => setOpenForm(false),
    handleToggleActive,
    handleDelete,
    refetch: loadDetail,
    orders,
    orderCount,
    isOrdersLoading,
    page,
    setPage,
    search,
    setSearch: changeSearch,
    showIds,
    setShowIds: changeShowIds,
  };
}
